import { useLocation } from 'react-router-dom';
import { entities } from '@shared/constants/entities';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import { createRoute, PATHS } from '@src/core/router/paths';

import {
  Briefcase,
  Category,
  Lamp,
  Math,
  MessageNotif,
  Notification,
  Personalcard,
  Profile2User,
  Setting2,
  TagUser,
  TaskSquare,
  Teacher,
} from 'iconsax-react';
import type { TNavLink } from './types';
import { useUserTypeCheck } from '@src/shared/hooks/use-user-type-check';

export const useIsActiveUrl = () => {
  const { pathname } = useLocation();
  const checkActivity = (url?: string) => new RegExp(`^${url}$`).test(pathname);
  return checkActivity;
};

export const useGenerateNavLinks = (): TNavLink[] => {
  const hasPermission = useHasPermission();
  const isPartner = useUserTypeCheck('PARTNER');

  const settingsPaths = [
    {
      isAllowed: hasPermission(entities.organization, 'view'),
      url: PATHS.SETTINGS_COMPANY_INFO,
    },
    {
      isAllowed: hasPermission(entities.organizationsubscription, 'view'),
      url: PATHS.SETTINGS_BILLING_A_SUBSCRIPTION,
    },
    {
      isAllowed: hasPermission(entities.integration, 'view'),
      url: PATHS.SETTINGS_INTEGRATION,
    },
    {
      isAllowed: true,
      url: PATHS.SETTINGS_BRANCHES,
    },
    {
      isAllowed: true,
      url: PATHS.SETTINGS_PREFERENCES,
    },
    {
      isAllowed: hasPermission(
        [entities.bankAccount, entities.eWalletAccount],
        'view'
      ),
      url: PATHS.SETTINGS_BANK_INFO,
    },
  ];

  const firstAllowedSettingRoute = settingsPaths.find(
    (path) => path.isAllowed
  )?.url;

  return [
    // {
    //   isAllowed: true,
    //   title: 'Dashboard',
    //   url: createRoute(PATHS.DASHBOARDS),
    //   icon: Category,
    //   isActive: true,
    // },
    {
      title: 'Dashboard',
      badgeLabel: 'Beta',
      icon: Category,
      url: createRoute(`${PATHS.DASHBOARDS}/lead-management`),
      isAllowed: !isPartner,
    },
    {
      title: 'Programs Managements',
      icon: Teacher,
      items: [
        {
          isAllowed: hasPermission(entities.program, 'view'),
          title: 'Programs',
          url: createRoute(PATHS.PROGRAMS),
        },
        {
          isAllowed: hasPermission(entities.program, 'add'),
          title: 'Add Programs',
          url: createRoute(PATHS.ADD_PROGRAM),
        },
      ].filter((link) => link.isAllowed),
    },
    {
      isAllowed: hasPermission(entities.lead, 'view'),
      title: 'Lead',
      icon: TagUser,
      items: [
        {
          isAllowed: hasPermission(entities.lead, 'view'),
          title: 'Leads',
          url: createRoute(PATHS.LEADS),
        },
        {
          isAllowed: hasPermission(entities.lead, 'view'),
          title: 'Follow Ups',
          url: createRoute(PATHS.LEADS_FOLLOW_UPS),
        },
      ],
    },
    {
      title: 'Participants',
      icon: Profile2User,
      items: [
        {
          isAllowed: true,
          title: 'Applications',
          url: createRoute(PATHS.PARTICIPANTS),
        },
        {
          isAllowed:
            hasPermission(entities.participantStageProgress, 'view') &&
            hasPermission(entities.stage, 'view'),
          title: 'Stages',
          url: createRoute(PATHS.STAGES),
        },
        {
          isAllowed: hasPermission(entities.placementApplication, 'view'),
          title: 'Placements',
          url: createRoute(PATHS.PARTICIPANTS_PLACEMENTS),
        },
        {
          isAllowed: true,
          title: 'All Participants',
          url: createRoute(PATHS.ALL_PARTICIPANTS),
        },
      ].filter((link) => link.isAllowed),
    },
    {
      isAllowed: hasPermission(entities.task, 'view'),
      title: 'Tasks',
      url: createRoute(PATHS.TASKS),
      icon: TaskSquare,
      entity: entities.task,
    },
    {
      title: 'Accounting',
      icon: Math,
      items: [
        {
          isAllowed: hasPermission(entities.invoice, 'view'),
          title: 'Invoices',
          url: createRoute(PATHS.ACCOUNTING_INVOICES),
          entity: entities.invoice,
        },
        {
          isAllowed: hasPermission(entities.payment, 'view'),
          title: 'Payments',
          url: createRoute(PATHS.ACCOUNTING_PAYMENTS),
          entity: entities.payment,
        },
        {
          isAllowed: hasPermission(entities.product, 'view'),
          title: 'Products',
          url: createRoute(PATHS.ACCOUNTING_PRODUCTS),
          entity: entities.product,
        },
      ].filter((link) => link.isAllowed),
    },
    {
      title: 'Placements',
      icon: Briefcase,
      items: [
        {
          isAllowed: hasPermission(entities.placement, 'view'),
          title: 'View Placements',
          url: createRoute(PATHS.PLACEMENTS),
        },
        {
          isAllowed: hasPermission(entities.placement, 'add'),
          title: 'Add Placement',
          url: createRoute(PATHS.CREATE_PLACEMENT),
        },
        {
          isAllowed: hasPermission(entities.placementHostCompany, 'view'),
          title: 'Host Companies',
          url: createRoute(PATHS.HOST_COMPANIES),
        },
      ].filter((link) => link.isAllowed),
    },
    {
      title: 'Reminders',
      icon: Lamp,
      badgeLabel: 'New',
      items: [
        {
          isAllowed: hasPermission(entities.reminderTemplate, 'view'),
          title: 'Overview',
          url: createRoute(PATHS.REMINDERS_OVERVIEW),
        },
        {
          isAllowed: hasPermission(entities.reminderTemplate, 'view'),
          title: 'All reminders',
          url: createRoute(PATHS.REMINDERS_ALL),
        },
        {
          isAllowed: hasPermission(entities.reminderTemplate, 'view'),
          title: 'Templates',
          url: createRoute(PATHS.REMINDERS_TEMPLATES),
        },
      ].filter((link) => link.isAllowed),
    },
    {
      isAllowed: hasPermission(entities.university, 'view'),
      title: 'Universities',
      url: createRoute(PATHS.UNIVERSITIES),
      icon: MessageNotif,
    },
    {
      isAllowed: hasPermission(entities.announcement, 'view'),
      title: 'Announcements',
      url: createRoute(PATHS.ANNOUNCEMENTS),
      icon: Notification,
    },
    {
      title: 'System Users',
      icon: Personalcard,
      items: [
        {
          isAllowed: hasPermission(entities.user, 'view'),
          title: 'Users & Partners',
          url: createRoute(PATHS.USERS),
          badgeLabel: 'New',
        },
        {
          isAllowed: hasPermission(entities.role, 'view'),
          title: 'Roles & Permissions',
          url: createRoute(PATHS.ROLES),
        },
      ].filter((link) => link.isAllowed),
    },
    // {
    //   isAllowed: true,
    //   title: 'Knowledge Center',
    //   url: createRoute(PATHS.KNOWLEDGECENTER),
    //   icon: MessageNotif,
    // },
    {
      isAllowed: !!firstAllowedSettingRoute,
      title: 'Settings',
      url: createRoute(firstAllowedSettingRoute as string),
      icon: Setting2,
    },
  ].filter((link) => link.items?.length || link.isAllowed);
};
